/**
 * Helpers for jobs/list catalog columns and view preference payloads.
 */

export function columnExistsOnTable(item) {
  if (!item || typeof item !== 'object') {
    return false;
  }
  if (item.showOnTable === false) {
    return false;
  }
  if (item.includeInTable === false) {
    return false;
  }
  if (item.enabled === false) {
    return false;
  }
  return true;
}

export function getColumnId(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }
  return item.name || item.column || item.accessorKey || null;
}

export function filterCatalogForTable(schema = []) {
  if (!Array.isArray(schema)) {
    return [];
  }
  return schema.filter(columnExistsOnTable);
}

/**
 * Flatten catalog items that may nest children (e.g. address groups).
 */
export function flattenCatalogItems(items = []) {
  if (!Array.isArray(items)) {
    return [];
  }
  const out = [];
  items.forEach(item => {
    if (!item || typeof item !== 'object') {
      return;
    }
    const { children } = item;
    if (Array.isArray(children) && children.length > 0) {
      children.forEach(child => {
        if (child && typeof child === 'object') {
          out.push(child);
        }
      });
      return;
    }
    out.push(item);
  });
  return out;
}

/**
 * Preference config may include hidden columns (`active: false`).
 * Table schema should only use active columns (missing `active` = visible).
 */
export function activeViewColumnsOnly(columns = []) {
  if (!Array.isArray(columns)) {
    return [];
  }
  return columns.filter(item => item?.active !== false);
}

/**
 * Extract column array from a views GET config / catalog payload.
 * Prefer array config (catalog), else config.columns (preference).
 */
export function extractViewColumns(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!payload || typeof payload !== 'object') {
    return [];
  }
  const root =
    payload.data &&
    typeof payload.data === 'object' &&
    !Array.isArray(payload.data)
      ? payload.data
      : payload;

  if (Array.isArray(root.config)) {
    return root.config;
  }
  if (Array.isArray(root.config?.columns)) {
    return root.config.columns;
  }
  if (Array.isArray(root.columns)) {
    return root.columns;
  }
  if (Array.isArray(root.catalog)) {
    return root.catalog;
  }
  return [];
}

/**
 * recordVersion + sort from a views GET config response (for PUT body).
 */
export function extractViewConfigMeta(payload) {
  if (!payload || typeof payload !== 'object') {
    return { recordVersion: 1, sort: undefined };
  }
  const root =
    payload.data &&
    typeof payload.data === 'object' &&
    !Array.isArray(payload.data)
      ? payload.data
      : payload;

  const sort =
    root.config &&
    typeof root.config === 'object' &&
    !Array.isArray(root.config)
      ? root.config.sort
      : undefined;

  return {
    recordVersion:
      typeof root.recordVersion === 'number' ? root.recordVersion : 1,
    sort,
  };
}

/**
 * Preserve current visible order; append newly selected ids in catalog order.
 */
export function buildOrderedColumnSelection({
  selectedIds = [],
  currentVisibleIds = [],
  catalogIds = [],
}) {
  const selected = new Set(selectedIds);
  const ordered = [];

  currentVisibleIds.forEach(id => {
    if (selected.has(id) && !ordered.includes(id)) {
      ordered.push(id);
    }
  });

  catalogIds.forEach(id => {
    if (selected.has(id) && !ordered.includes(id)) {
      ordered.push(id);
    }
  });

  return ordered;
}

export function isColumnOrderingLocked(item) {
  return item?.enableColumnOrdering === false;
}

/**
 * Count of contiguous enableColumnOrdering:false items from the catalog start.
 * Nothing may be dropped/moved above this prefix.
 */
export function getOrderingLockedPrefixLength(catalogItems = []) {
  const firstMovableIndex = catalogItems.findIndex(
    item => !isColumnOrderingLocked(item),
  );
  if (firstMovableIndex === -1) {
    return catalogItems.length;
  }
  return firstMovableIndex;
}

/**
 * Mirror backend pinLockedColumns for id lists: keep ordering-locked ids at
 * their catalog indexes; movable ids fill the remaining slots in relative order.
 */
export function pinColumnOrder(orderedIds = [], catalogItems = []) {
  if (!Array.isArray(catalogItems) || catalogItems.length === 0) {
    return orderedIds;
  }

  const catalogById = new Map();
  catalogItems.forEach(item => {
    const id = getColumnId(item);
    if (id) {
      catalogById.set(id, item);
    }
  });

  const seen = new Set();
  const movable = [];
  orderedIds.forEach(id => {
    if (!id || seen.has(id) || !catalogById.has(id)) {
      return;
    }
    seen.add(id);
    if (!isColumnOrderingLocked(catalogById.get(id))) {
      movable.push(id);
    }
  });

  const result = [...movable];
  catalogItems.forEach((item, catalogIndex) => {
    if (!isColumnOrderingLocked(item)) {
      return;
    }
    const id = getColumnId(item);
    if (!id || !orderedIds.includes(id)) {
      return;
    }
    result.splice(Math.min(catalogIndex, result.length), 0, id);
  });

  orderedIds.forEach(id => {
    if (id && catalogById.has(id) && !result.includes(id)) {
      result.push(id);
    }
  });

  return result;
}

/**
 * Config column ids first (in config order), then remaining catalog ids.
 */
export function buildDialogOrderFromConfig({
  catalogIds = [],
  configIds = [],
} = {}) {
  const catalogSet = new Set(catalogIds);
  const ordered = [];
  const seen = new Set();

  configIds.forEach(id => {
    if (id && catalogSet.has(id) && !seen.has(id)) {
      ordered.push(id);
      seen.add(id);
    }
  });

  catalogIds.forEach(id => {
    if (id && !seen.has(id)) {
      ordered.push(id);
      seen.add(id);
    }
  });

  return ordered;
}

/**
 * Re-order items to match preferred ids; append any leftovers in original order.
 */
export function orderItemsByIds(getId, items = [], orderedIds = []) {
  if (!Array.isArray(items) || typeof getId !== 'function') {
    return items;
  }
  const byId = new Map();
  items.forEach(item => {
    const id = getId(item);
    if (id) {
      byId.set(id, item);
    }
  });
  const ordered = [];
  const seen = new Set();
  orderedIds.forEach(id => {
    if (id && byId.has(id) && !seen.has(id)) {
      ordered.push(byId.get(id));
      seen.add(id);
    }
  });
  items.forEach(item => {
    const id = getId(item);
    if (id && !seen.has(id)) {
      ordered.push(item);
      seen.add(id);
    }
  });
  return ordered;
}

export function orderSchemaByColumnIds(schema = [], orderedIds = []) {
  return orderItemsByIds(getColumnId, schema, orderedIds);
}

export function moveArrayItem(list, fromIndex, toIndex) {
  if (
    !Array.isArray(list) ||
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= list.length ||
    toIndex >= list.length
  ) {
    return list;
  }
  const next = [...list];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}
